import type { PortSecurityFormData } from "@features/port-security/schema/portSecurityFormSchema";
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface PortSecurityFormMethods {
  setField<K extends keyof PortSecurityFormData>(key: K, val: PortSecurityFormData[K]): void;
  getField<K extends keyof PortSecurityFormData>(key: K): PortSecurityFormData[K];
  setAll(data: Partial<PortSecurityFormData>): void;
  resetForm(): void;
}

interface PortSecurityFormState extends PortSecurityFormData, PortSecurityFormMethods {}

const defaultValues: PortSecurityFormData = {
  // Bio Data
  fullNameEn: "",
  fullNameAr: "",
  surname: "",
  alias: "",
  placeOfBirth: "",

  dateOfBirth: "",
  pastNationality: "",
  presentNationality: "",
  presentNationalityArabic: "",
  sect: "",
  religion: "",
  placeOfEntry: "",
  dateOfEntry: "",
  sponsorUponEntry: "",

  // Current Sponsor Data
  currentSponsor: "",
  occupation: "",
  occupationAr: "",
  officeFax: "",
  presentWorkplace: "",
  bank: "",
  officeTelephone: "",
  salary: "",

  // Particulars of Passport
  passportPlaceOfIssue: "",
  passportNumber: "",
  passportExpiryDate: "",
  passportDateOfIssue: "",

  emiratesIdNumber: "",

  // Residence / VISA
  visaPlaceOfIssue: "",
  residenceVisaNo: "",
  visaType: "",
  visaDateOfIssue: "",
  visaExpiryDate: "",

  // Academic Qualifications
  academicQualification: "",
  placeOfStudy: "",
  university: "",
  languagesKnown: "",
  certificateDate: "",

  // Social Status
  spouseName: "",
  spouseNationality: "",
  spouseBirthPlace: "",
  spouseBirthDate: "",
  spouseOccupation: "",
  spouseWorkPlace: "",

  // Children
  children: [""],

  // Father
  fatherName: "",
  fatherNationality: "",
  fatherBirthPlace: "",
  fatherBirthDate: "",
  fatherWorkPlace: "",
  fatherOccupation: "",

  // Mother
  motherName: "",
  motherNationality: "",
  motherBirthPlace: "",
  motherBirthDate: "",
  motherWorkPlace: "",
  motherOccupation: "",

  // Other Blood Relations
  bloodRelations: [{ name: "", relation: "", nationality: "", workPlace: "" }],

  // Friends
  friends: [{ name: "", nationality: "", workPlace: "" }],

  // Residence
  zone: "",
  ownerName: "",
  phoneNo: "",
  street: "",
  mobileNo: "",
  buildingNo: "",
  pagerNo: "",
  flatNo: "",
  poBoxNo: "",
  floorNo: "",

  // Previous occupations
  prevOccupations: [""],

  //Countries Worked In
  countriesWorkedIn: [""],

  // Countries Visited
  countriesVisited: [""],

  //Particulars of Vehicle
  typeOfVehicle: "",
  licenseNumber: "",
  placeOfIssue: "",
  licensedBy: "",
  dateOfIssue: "",
  plateNumber: "",
  dateOfLicenseExpiry: "",
  plateCode: "",

  //Military Field
  hasServed: false,
  countryName: "",
  serviceType: "",
  rank: "",
  durationOfService: "",
  militaryServiceStartDate: "",
  militaryServiceEndDate: "",

  //Signature
  signatureAgreement: false,
  signatureName: "",
  signatureDate: new Date().toISOString().slice(0, 10), // Today's date in YYYY-MM-DD format

  // Visit Details and Documents
  photograph: [],
  passportFront: [],
  passportBack: [],
  emiratesIdFront: [],
  emiratesIdBack: [],
  visa: [],
  // FOTT Entry
  FOTTEntry: false,

  // Visit Details
  entryPurposeEnglish: "",
  entryPurposeArabic: "",
  emailAddress: "",
  mobileNumber: "",
  securityClearanceValidity: "",
  sponsorCompanyEnglish: "",
  sponsorCompanyArabic: "",
  visitorCompanyEnglish: "",
  visitorCompanyArabic: "",
  uidNumber: "",
  requestingSponsorEmailAddress: "",
  gdpr: true,
};

export const usePortSecurityFormStore = create<PortSecurityFormState>()(
  persist(
    (set, get) => ({
      ...defaultValues,
      setField: (key, val) => set((s) => ({ ...s, [key]: val })),
      getField: (key) => get()[key],
      setAll: (data) => set((s) => ({ ...s, ...data })),
      resetForm: () => set(() => ({ ...defaultValues })),
    }),
    { name: "security-clearance-form" },
  ),
);
